import {
  Button,
  Container,
  Stack,
  Text,
  TextInput,
  Title,
  Anchor,
  Alert,
  Image,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useState } from "react";
import { IconAlertCircle, IconMail, IconCheck } from "@tabler/icons-react";
import { URL } from "../../services/url";
import axios from "axios";

interface ForgotPasswordResponse {
  success: boolean;
  response: string;
}

const ForgotPassword = () => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post<ForgotPasswordResponse>(`${URL}forgot-password/`, {
        email,
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setSuccess(true);
      } else {
        setError(response.data.response || "Failed to send reset email");
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      console.error("Forgot password failed", error);
      if (error.response?.data?.response) {
        setError(error.response.data.response);
      } else {
        setError("An error occurred. Please try again later.");
      }
    }
  };

  if (success) {
    return (
      <Container
        size={isMobile ? "xs" : "sm"}
        px={isMobile ? "md" : "xl"}
        py={isMobile ? "xl" : "80px"}
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Stack gap="xl" align="center" w="100%" maw={400} mx="auto">
          <Stack align="center" gap="md" ta="center">
            <Image 
              src="/landingimg.png" 
              maw={isMobile ? 120 : 150} 
            />
            <Stack gap="xs" align="center">
              <Title order={1} size={isMobile ? "h2" : "h1"} fw={700}>
                Kabbie
              </Title>
            </Stack>
          </Stack>

          <Alert
            icon={<IconCheck size={20} />}
            color="green"
            variant="light"
            radius="md"
            w="100%"
          >
            <Stack gap="xs">
              <Text fw={600}>Check your email</Text>
              <Text size="sm">
                We've sent password reset instructions to {email}
              </Text>
            </Stack>
          </Alert>

          <Stack gap="md" w="100%" ta="center">
            <Text size="sm" c="dimmed">
              Didn't receive the email? Check your spam folder or try again.
            </Text>
            
            <Button
              variant="light"
              onClick={() => {
                setSuccess(false);
                setEmail("");
              }}
            >
              Try another email
            </Button>

            <Anchor href="/signin" size="sm" c="dimmed">
              ← Back to Sign In
            </Anchor>
          </Stack>
        </Stack>
      </Container>
    );
  }

  return (
    <Container
      size={isMobile ? "xs" : "sm"}
      px={isMobile ? "md" : "xl"}
      py={isMobile ? "xl" : "80px"}
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Stack gap="xl" align="center" w="100%">
        <Stack align="center" gap="md" ta="center">
          <Image 
            src="/landingimg.png" 
            maw={isMobile ? 120 : 150} 
          />
          <Stack gap="xs" align="center">
            <Title order={1} size={isMobile ? "h2" : "h1"} fw={700}>
              Kabbie
            </Title>
          </Stack>
        </Stack>

        <Stack gap="lg" w="100%" maw={400}>
          <Stack gap="xs" ta="center">
            <Title order={2} size="h3">
              Reset Your Password
            </Title>
            <Text size="sm" c="dimmed">
              Enter your email address and we'll send you instructions to reset your password
            </Text>
          </Stack>

          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <TextInput
                label="Email Address"
                placeholder="Enter your email"
                type="email"
                size="md"
                required
                leftSection={<IconMail size={18} />}
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                onFocus={() => setError("")}
                styles={{
                  label: { marginBottom: "0.5rem" },
                }}
              />

              {error && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="red"
                  variant="light"
                  radius="md"
                >
                  <Text size="sm">{error}</Text>
                </Alert>
              )}

              <Button
                fullWidth
                size="lg"
                mt="md"
                type="submit"
                variant="gradient"
                gradient={{ from: '#000000', to: '#333333', deg: 145 }}
                loading={loading}
                loaderProps={{ type: "oval" }}
                disabled={!email}
              >
                Send Reset Instructions
              </Button>
            </Stack>
          </form>

          <Stack gap="sm" mt="md" ta="center">
            <Anchor href="/signin" size="sm" c="dimmed">
              ← Back to Sign In
            </Anchor>
            <Text size="sm" c="dimmed">
              Don't have an account?{" "}
              <Anchor href="/signup" fw={600}>
                Create Account
              </Anchor>
            </Text>
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
};

export default ForgotPassword;